import { noop } from '@crocodille/util';
import IconExpand from '@icons/expand';
import cn from 'classnames';
import { AnimatePresence, LayoutGroup, motion } from 'framer-motion';
import {
  createContext,
  useCallback,
  useContext,
  useId,
  useMemo,
  useState,
} from 'react';

import styles from './accordion.module.scss';

const AccordionGroupContext = createContext<{
  inGroup: boolean;
  openChild: string | null;
  setOpenChild: (openChild: string | null) => void;
}>({
  inGroup: false,
  openChild: null,
  setOpenChild: noop,
});

export const AccordionGroup: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [openChild, setOpenChild] = useState<string | null>(null);

  const ctx = useMemo(
    () => ({ inGroup: true, openChild, setOpenChild }),
    [openChild, setOpenChild],
  );

  return (
    <AccordionGroupContext.Provider value={ctx}>
      <LayoutGroup>{children}</LayoutGroup>
    </AccordionGroupContext.Provider>
  );
};

export interface AccordionProps {
  readonly title: string;
  readonly subTitle?: React.ReactNode;
  readonly className?: string;
  readonly invalid?: boolean;
  readonly initialOpen?: boolean;
}

const Accordion: React.FC<React.PropsWithChildren<AccordionProps>> = ({
  children,
  title,
  subTitle,
  className,
  invalid,
  initialOpen = false,
}) => {
  const id = useId();
  const [selfOpen, setSelfOpen] = useState(initialOpen);
  const { inGroup, openChild, setOpenChild } = useContext(
    AccordionGroupContext,
  );

  const open = inGroup ? openChild === id : selfOpen;

  const toggle = useCallback(() => {
    if (inGroup) {
      setOpenChild(open ? null : id);
    } else {
      setSelfOpen((open) => !open);
    }
  }, [inGroup, open, setOpenChild, id]);

  return (
    <motion.div
      className={cn(
        styles.container,
        {
          [styles.isInvalid]: invalid,
        },
        className,
      )}
      layout
    >
      <motion.div layout className={styles.button} onClick={toggle}>
        <span className={styles.title}>{title}</span>
        {subTitle ? <span className={styles.subTitle}>{subTitle}</span> : null}
        <IconExpand
          className={cn(styles.expandIcon, {
            [styles.isOpen]: open,
          })}
        />
      </motion.div>
      <AnimatePresence initial={false}>
        {open ? (
          <motion.div
            key="item-container"
            layout="position"
            exit={{ opacity: 0 }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            {children}
          </motion.div>
        ) : null}
      </AnimatePresence>
    </motion.div>
  );
};

export default Accordion;
